import { Mail, Phone, Instagram, Facebook, Linkedin, HelpCircle, CheckCircle, GraduationCap } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import domusFlexiLogoAzzurro from "@/assets/logo-domus-flexi-footer-complete.png";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

export const Footer = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <footer className="bg-foreground text-background py-12 px-4">
      <div className="container mx-auto max-w-4xl">

        <div className="grid md:grid-cols-3 gap-8">

          {/* Affitti per Città */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold mb-4 text-lg">{t("footer.cityRentalsTitle")}</h4>
            <div className="space-y-2">
              <Link 
                to="/affitti-italia" 
                className="block text-background/80 hover:text-background transition-colors"
              >
                {t("footer.rentalsItaly")}
              </Link>
              <Link 
                to="/affitti-milano" 
                className="block text-background/80 hover:text-background transition-colors"
              >
                {t("footer.rentalsMilan")}
              </Link>
              <Link 
                to="/affitti-napoli" 
                className="block text-background/80 hover:text-background transition-colors"
              >
                {t("footer.rentalsNaples")}
              </Link>
              <Link 
                to="/affitti-roma" 
                className="block text-background/80 hover:text-background transition-colors flex items-center gap-2"
              >
                {t("footer.rentalsRome")}
                <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">{t("footer.comingSoon")}</span>
              </Link>
            </div>
            <h4 className="font-semibold mb-4 text-lg mt-6">{t("footer.resourcesTitle")}</h4>
            <div className="space-y-2">
              <Link 
                to="/chi-siamo" 
                className="block text-background/80 hover:text-background transition-colors"
              >
                {t("footer.aboutDomusFlexi")}
              </Link>
              <Link 
                to="/come-funziona" 
                className="block text-background/80 hover:text-background transition-colors"
              >
                {t("footer.howDomusFlexiWorks")}
              </Link>
              <Link 
                to="/sicurezza-verifiche" 
                className="block text-background/80 hover:text-background transition-colors"
              >
                {t("footer.securityVerifications")}
              </Link>
              <Link 
                to="/community-studenti" 
                className="flex items-center gap-2 text-background/80 hover:text-background transition-colors group"
              >
                <GraduationCap className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                {t("footer.studentCommunity")}
              </Link>
            </div>
          </div>

          {/* Contact Info */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold mb-4 text-lg">{t("footer.contact")}</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-center md:justify-start">
                <Mail className="w-4 h-4 mr-2 text-primary" />
                <a href="mailto:customerservice@domusflexi.com" className="text-background/80 hover:text-background transition-colors">
                  customerservice@domusflexi.com
                </a>
              </div>
              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-primary" />
                  <a href="tel:+393793438934" className="text-background/80 hover:text-background transition-colors">
                    +39 379 343 8934
                  </a>
                </div>
                <a 
                  href="https://wa.me/393793438934" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full p-1.5 transition-colors"
                  aria-label="WhatsApp Business"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold mb-4 text-lg">{t("footer.followUs")}</h4>
            <div className="flex justify-center md:justify-start space-x-4">
              <a 
                href="https://instagram.com/domusflexi" 
                className="bg-background/10 hover:bg-primary rounded-full p-2 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.facebook.com/domusflexi" 
                className="bg-background/10 hover:bg-primary rounded-full p-2 transition-colors"
                aria-label="Facebook Domus Flexi"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/domus-flexi/?viewAsMember=true" 
                className="bg-background/10 hover:bg-primary rounded-full p-2 transition-colors"
                aria-label="LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-background/20 mt-8 pt-6">
          {/* Logo e Riferimenti legali */}
          <div className="mb-6 text-center">
            <Link to="/" className="inline-flex items-center gap-3 mb-3 hover:opacity-80 transition-opacity">
              <img 
                src={domusFlexiLogoAzzurro} 
                alt="Logo Domus Flexi – poltrona e lampada stilizzata" 
                className="h-[120px] md:h-[200px] w-auto object-contain"
                width={164}
                height={200}
              />
            </Link>
            
            {/* Frase identitaria SEO */}
            <p className="text-background/90 text-sm md:text-base font-medium max-w-xl mx-auto mb-4 leading-relaxed">
              {t("footer.identityPhrase")}
            </p>
            
            {/* Micro-trust signals */}
            <div className="flex flex-wrap justify-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/10 text-xs text-background/90 font-medium">
                <CheckCircle className="h-3.5 w-3.5 text-primary" />
                {t("footer.trustSignal1")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/10 text-xs text-background/90 font-medium">
                <CheckCircle className="h-3.5 w-3.5 text-primary" />
                {t("footer.trustSignal2")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/10 text-xs text-background/90 font-medium">
                <CheckCircle className="h-3.5 w-3.5 text-primary" />
                {t("footer.trustSignal3")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/10 text-xs text-background/90 font-medium">
                <CheckCircle className="h-3.5 w-3.5 text-primary" />
                {t("footer.trustSignal4")}
              </span>
            </div>
            
            <p className="text-background/70 text-xs leading-relaxed">
              <strong>{t("footer.companyName", "Domus Flexi S.r.l. – Società Unipersonale")}</strong><br />
              {t("footer.registeredOffice", "Sede legale: Via Carlo Botta 35, 20135 Milano (MI) – Italia")}<br />
              {t("footer.taxCode", "Codice Fiscale / P.IVA: 14380880964 | Numero REA: MI – 2778985")}<br />
              {t("footer.certifiedEmail", "PEC: domusflexi@unapec.it | Amministratore Unico e Socio Unico: CREA Paolo")}
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-background/60 text-sm text-center md:text-left">
              © 2025 Domus Flexi. {t("footer.allRightsReserved")} | 
              <a href="/privacy-policy" className="hover:text-background transition-colors ml-1">{t("footer.privacyPolicy")}</a> | 
              <a href="/termini-servizio" className="hover:text-background transition-colors ml-1">{t("footer.termsOfService")}</a> | 
              <a href="/cookie-policy" className="hover:text-background transition-colors ml-1">{t("footer.cookiePolicy")}</a> | 
              <button 
                onClick={() => { 
                  localStorage.removeItem("cookie-consent"); 
                  localStorage.removeItem("cookie-consent-date");
                  window.location.reload(); 
                }} 
                className="hover:text-background transition-colors ml-1 underline cursor-pointer bg-transparent border-0 p-0"
              >
                {t("footer.cookieManage")}
              </button>
            </p>
            
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="bg-background/10 hover:bg-primary border-background/20 text-background hover:text-primary-foreground"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  {t("footer.faq")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-background">
                <DialogHeader>
                  <DialogTitle className="flex items-center text-2xl">
                    <div className="bg-primary/10 rounded-full p-2 mr-3">
                      <HelpCircle className="w-6 h-6 text-primary" />
                    </div>
                    {t("footer.faq")}
                  </DialogTitle>
                  <DialogDescription>
                    {t("footer.faqDescription")}
                  </DialogDescription>
                </DialogHeader>
                
                <Accordion type="single" collapsible className="w-full space-y-4 mt-4">
                  <AccordionItem value="item-1" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Domus Flexi è un intermediario?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      No. Domus Flexi non svolge attività di intermediazione immobiliare. È una piattaforma digitale che offre strumenti per gestire affitti in autonomia, con supporto operativo e verifiche documentali.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Ci sono costi per partecipare?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      La registrazione è gratuita. Alcuni servizi avanzati, come la certificazione della solvibilità o la verifica fisica dell'immobile, possono prevedere costi aggiuntivi visibili nella sezione{" "}
                      <Link to="/servizi-premium" className="text-primary hover:underline">
                        Servizi Premium
                      </Link>.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Come avviene la verifica dell'identità?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Domus Flexi verifica l'identità del proprietario tramite email, telefono e documentazione coerente. Per gli inquilini, la verifica può avvenire tramite strumenti digitali e documenti forniti volontariamente. Scopri di più nella pagina{" "}
                      <Link to="/sicurezza-verifiche" className="text-primary hover:underline">
                        Sicurezza e Verifiche
                      </Link>.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      È sicuro affittare senza agenzia?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Sì, perché Domus Flexi è una piattaforma che lavora solo con privati verificati. Ogni annuncio è controllato, ogni contatto è tracciato, e l'admin supervisiona ogni fase. In più, offriamo assistenza contrattuale e supporto diretto.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-5" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Come funziona la protezione dei dati su Domus Flexi?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      I dati degli utenti sono visibili solo agli admin autorizzati. Le identità vengono rivelate solo dopo consenso esplicito. Domus Flexi rispetta il GDPR e utilizza sistemi di accesso protetti.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-6" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Che tipo di sistemazioni posso proporre o cercare?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Puoi proporre o cercare appartamenti, stanze, case indipendenti e soluzioni flessibili per affitti transitori o regolari, in base alla disponibilità e alle regole della piattaforma.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-7" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Come posso attivare i servizi aggiuntivi?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      I servizi aggiuntivi si attivano dalla pagina{" "}
                      <Link to="/servizi-premium" className="text-primary hover:underline">
                        Servizi Premium
                      </Link>{" "}
                      oppure scrivendo a{" "}
                      <a href="mailto:customerservice@domusflexi.com" className="text-primary hover:underline">
                        customerservice@domusflexi.com
                      </a>.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-8" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Domus Flexi applica commissioni all'inquilino?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      No. Domus Flexi non applica commissioni di intermediazione all'inquilino. Alcuni servizi opzionali possono prevedere costi visibili prima dell'attivazione.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-9" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Qual è la differenza tra contratto transitorio e contratto regolare?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Il contratto transitorio ha durata limitata e motivazione specifica. Il contratto regolare è più stabile e continuativo. Trovi una guida completa nella sezione{" "}
                      <Link to="/come-funziona" className="text-primary hover:underline">
                        Come funziona
                      </Link>.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-10" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Come funziona la verifica degli annunci e dei proprietari?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Domus Flexi verifica gli annunci e i proprietari tramite controlli documentali, badge di verifica e — quando disponibile — verifica fisica. Scopri di più nella pagina{" "}
                      <Link to="/sicurezza-verifiche" className="text-primary hover:underline">
                        Sicurezza e Verifiche
                      </Link>.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-11" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Domus Flexi garantisce la sicurezza degli affitti?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Domus Flexi non garantisce direttamente la sicurezza dell'affitto, ma offre strumenti per verificare identità, documenti, solvibilità e immobili. Questi strumenti aumentano la trasparenza e riducono i rischi. Approfondisci nella pagina{" "}
                      <Link to="/sicurezza-verifiche" className="text-primary hover:underline">
                        Sicurezza e Verifiche
                      </Link>.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-12" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Chi gestisce il contratto di affitto?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Il contratto è gestito direttamente tra proprietario e inquilino. Domus Flexi fornisce modelli standard, linee guida e strumenti per facilitare la stipula.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-13" className="bg-muted/50 rounded-lg px-6 border border-border">
                    <AccordionTrigger className="text-foreground hover:text-foreground/80 text-left font-semibold">
                      Quali documenti sono consigliati per procedere con un affitto?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      Si consiglia di avere: documento d'identità, visura catastale, planimetria, contratto precedente (se disponibile), certificazione di solvibilità. Domus Flexi può aiutarti a raccoglierli.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground text-center">
                    <strong>{t("footer.faqQuestions.note")}</strong> {t("footer.faqQuestions.noteText")}{" "}
                    <Link to="/servizi-premium" className="text-primary hover:underline">
                      {t("footer.faqQuestions.participationCostsLink")}
                    </Link>{" "}
                    {t("footer.faqQuestions.noteEnd")}{" "}
                    <a href="mailto:customerservice@domusflexi.com" className="text-primary hover:underline">
                      customerservice@domusflexi.com
                    </a>.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </footer>
  );
};